import { post, get } from './http';
import { HttpError } from './http';
import type { AuthResponse } from '../types/api';

export interface RequestPasswordResetPayload {
    email: string;
    brandId: number;
}

/**
 * `POST password-resets` (API-519): emails a set-password link to the account `email` belongs to,
 * for the brand the visitor is on. Anonymous, v3, `Cache-Control: no-store`. The link opens
 * UX-799's `/ux/set-password`, which `checkPasswordResetStatus` checks and `setPassword` redeems.
 *
 * The API answers 200 for every well-formed request to an existing brand — whether or not an
 * account matched, and whether or not the email was sent — so UX-798's anti-enumeration
 * requirement is met by the API itself and needs no help here. (The v2 `POST
 * emails/password-resets` this replaced 404'd when no account matched, which this function used to
 * swallow.)
 *
 * Failures arrive as `HttpError`, whose `cause` is the `{ code, status, title?, errors? }` body:
 * - 400 with `errors.email` / `errors.brandId`: the request was malformed. Nothing was read, so it
 *   says nothing about whether an account exists.
 * - 400 without `errors` ("Unable to request a password reset."): the request could not be
 *   processed. Also independent of whether an account exists.
 * - 404 ("Brand not found"): no brand has this id.
 *
 * The caller shows one generic failure for all of them, so none is singled out here.
 */
export async function requestPasswordReset(payload: RequestPasswordResetPayload): Promise<void> {
    await post('password-resets', payload);
}

export interface PasswordResetStatus {
    valid: boolean;
    /**
     * The email the link was issued to. Sent only with `valid: false`, when the link has
     * expired but the account is still active: UX-799's "Invalid Link" screen prefills a
     * fresh reset request with it.
     */
    email?: string;
}

/**
 * `GET password-resets/{guid}/status` (API-521): whether a password-reset/set-password link
 * is still valid, without consuming it — UX-799 uses it to decide between the Set Password
 * form and the Invalid Link screen when the page first loads. Anonymous, v3,
 * `Cache-Control: no-store`.
 *
 * - 200 `{ valid: true }`: the link can be redeemed.
 * - 200 `{ valid: false, email }`: expired, and the account is still active.
 * - 200 `{ valid: false }`: unknown, used, superseded or otherwise unusable.
 *
 * Failures arrive as `HttpError`: 404 (no body) for a malformed id, 400 when the check
 * itself failed. The caller treats any failure as an invalid link.
 */
export async function checkPasswordResetStatus(guid: string): Promise<PasswordResetStatus> {
    const response = await get<PasswordResetStatus>(`password-resets/${guid}/status`);
    return response.data!;
}

/**
 * `POST password-resets/{guid}` (API-520): sets a new password for the account behind
 * `guid` and signs the user in (UX-799: "the user should be signed in and taken to their
 * dashboard"). Anonymous, v3, `Cache-Control: no-store`. Resolves to the same unwrapped
 * `AuthResponse` as `Auth/login`, so the caller can hand it straight to
 * `Api.sessionUpdate()`. For the Owner of a suspended business (its own suspension or its
 * parent's) that 200 body carries `suspended: true` (ROBO-478, `AuthResponse.suspended`), and the
 * caller routes it through `postLoginDestination` to billing, as sign-in does.
 *
 * Failures arrive as `HttpError`, whose `cause` is the `{ code, status, title?, errors? }` body:
 * - 400 with `errors.password`: the password was refused (missing, under 10 or over 32
 *   characters, or contains U+00A0/U+202C/U+202D). The link is still usable; see
 *   `setPasswordFieldError`.
 * - 400 without `errors.password` ("Unable to set the password."): the redeem itself failed.
 * - 403 `{ code: 'BRAND_SUSPENDED', status: 403 }` (no `title`): the link is valid, but the user
 *   is a non-Owner, non-Admin user of a suspended business (its own suspension or its parent's).
 *   Normally nothing was changed and the link is still usable; only if the business was suspended
 *   during the request was the password already saved. Only that code is a suspension — test it
 *   with `isBrandSuspendedError` (`services/session-refusal.ts`), never the status alone.
 * - 404 ("This link is invalid or has expired."): unknown, used, superseded, expired or
 *   inactive. A malformed id is also a 404, with no body.
 * - 500: the password WAS set, but signing in failed.
 */
export async function setPassword(guid: string, password: string): Promise<AuthResponse> {
    const response = await post<AuthResponse, { password: string }>(`password-resets/${guid}`, {
        password
    });
    return response.data!;
}

/**
 * The API's first message for the password field from a 400, or `undefined` when the failure
 * is not a password refusal — so an empty result means the caller shows its generic failure.
 */
export function setPasswordFieldError(error: unknown): string | undefined {
    if (!(error instanceof HttpError) || error.status !== 400) return undefined;

    const body = error.cause;
    const errors =
        body !== null && typeof body === 'object'
            ? (body as Record<string, unknown>).errors
            : undefined;
    if (errors === null || typeof errors !== 'object' || Array.isArray(errors)) return undefined;

    const value = (errors as Record<string, unknown>).password;
    const message = Array.isArray(value)
        ? value.find((part): part is string => typeof part === 'string' && part.length > 0)
        : typeof value === 'string'
          ? value
          : undefined;
    return message || undefined;
}
